import { fetchFormlosFragment } from './formlos-fragment';

export type FormlosFrameKind = 'header' | 'footer';

interface FormlosPageFragments {
  header: string;
  footer: string;
}

const fragmentCache = new Map<string, { expires: number; request: Promise<FormlosPageFragments> }>();
const CACHE_TTL_MS = 300_000;

/** The two WordPress responses are the opening and closing halves of one HTML document. */
export function loadFormlosPage(origin: string): Promise<FormlosPageFragments> {
  const cached = fragmentCache.get(origin);

  if (cached && cached.expires > Date.now()) { return cached.request; }
  const { signal } = new AbortController();
  const headerRequest = fetchFormlosFragment(origin, 'header', signal);
  const footerRequest = fetchFormlosFragment(origin, 'footer', signal);
  const request = Promise.all([headerRequest, footerRequest]).then(([header, footer]) => {
    return {
      footer,
      header,
    };
  });

  fragmentCache.set(origin, {
    expires: Date.now() + CACHE_TTL_MS,
    request,
  });
  request.catch(() => {
    if (fragmentCache.get(origin)?.request === request) { fragmentCache.delete(origin); }
  });

  return request;
}

export function createFormlosFrameDocument(fragments: FormlosPageFragments,
  kind: FormlosFrameKind,
  origin: string): string {
  if (!(/<head\b[^>]*>/iu).test(fragments.header) || !(/<\/head>/iu).test(fragments.header)
    || !(/<body\b/iu).test(fragments.header) || !(/<\/body>/iu).test(fragments.footer)) {
    throw new Error('Formlos fragments are missing the required document shell');
  }
  const assetRoot = new URL('assets/formlos/', document.baseURI);
  const base = `<base href="${origin}/" target="_top">`;
  const style = `<link rel="stylesheet" href="${new URL('iframe.css', assetRoot)}">`;
  const bridge = `<script src="${new URL('iframe-bridge.js', assetRoot)}" data-formlos-embed="${kind}"></script>`;
  const opening = fragments.header
    .replace(/<head\b[^>]*>/iu, (tag) => { return `${tag}${base}`; })
    .replace(/<\/head>/iu, `${style}</head>`)
    .replace(/<body\b/iu, `<body data-formlos-embed="${kind}"`);
  const closing = fragments.footer
    // Relevanssi cleans search tracking parameters on full WordPress pages.
    // A srcdoc shell has no WordPress URL and cannot rewrite its history to one.
    .replace(/<script\b[^>]*>[\s\S]*?<\/script\s*>/giu, (script) => {
      if (script.includes('relevanssi_rt_regex') && script.includes('history.replaceState')) { return ''; }

      return script;
    })
    .replace(/<\/body>/iu, `${bridge}</body>`);

  return opening + closing;
}
